<!--
  组件：LocalMenuItem - 递归菜单项组件
  功能：支持无限层级的菜单递归渲染
  依赖：无
  使用场景：LocalMenu组件的递归子组件
-->
<template>
  <div class="menu-item-container">
    <!-- 有子菜单的情况 -->
    <div v-if="hasChildren" class="sub-menu-item">
      <div 
        class="sub-menu-title" 
        @click="$emit('toggle-sub-menu', menu.path)"
        :class="{ 
          // 修复三级目录高亮：当当前路由位于本菜单的任意后代时，高亮当前二级（或中间层级）菜单
          'active': isParentOfActivePath(menu.path), 
          'expanded': isOpen(menu.path),
          [`level-${level}`]: true
        }"
      >
        <span class="menu-text">{{ menu?.meta?.title }}</span>
        <svg 
          class="arrow-icon" 
          :class="{ 
            'rotate': isOpen(menu.path), 
            'expanded': isOpen(menu.path), 
            'active': isParentOfActivePath(menu.path) 
          }" 
          xmlns="http://www.w3.org/2000/svg" 
          xmlns:xlink="http://www.w3.org/1999/xlink" 
          width="8" 
          height="5.3330078125" 
          viewBox="0 0 8 5.3330078125" 
          fill="none"
        >
          <path d="M7.76248 1.6428L5.02136 4.86633C4.54639 5.4249 3.70944 5.4919 3.15199 5.01598C3.09976 4.9714 3.05107 4.92282 3.00634 4.87068L0.24032 1.64606C-0.117702 1.22868 -0.0702524 0.599513 0.346303 0.24078C0.526699 0.085425 0.756691 -2.96993e-16 0.994561 -8.57919e-16L7.00546 -1.21796e-16C7.55473 5.35526e-16 8 0.446155 8 0.996516C8 1.23338 7.91579 1.46251 7.76248 1.6428Z" style="mix-blend-mode:normal" fill="currentColor" />
        </svg>
      </div>
      <div 
        class="sub-menu-children" 
        v-show="isOpen(menu.path) || closingKeys.includes(menu.path)"
        :class="{ 'closing': closingKeys.includes(menu.path) }"
      >
        <LocalMenuItem
          v-for="child in visibleChildren"
          :key="child.path"
          :menu="child"
          :level="level + 1"
          :selected-keys="selectedKeys"
          :open-keys="openKeys"
          :closing-keys="closingKeys"
          @toggle-sub-menu="$emit('toggle-sub-menu', $event)"
          @menu-click="$emit('menu-click', $event)"
        />
      </div>
    </div>

    <!-- 无子菜单的情况 -->
    <div 
      v-else 
      class="menu-item"
      :class="{ 
        'active': selectedKeys.includes(menu.path), 
        [`level-${level}`]: true
      }"
      @click="$emit('menu-click', menu.path)"
    >
      {{ menu?.meta?.title }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'

const props = defineProps({
  menu: {
    type: Object,
    required: true
  },
  level: {
    type: Number,
    default: 0
  },
  selectedKeys: {
    type: Array,
    default: () => []
  },
  openKeys: {
    type: Array,
    default: () => []
  },
  closingKeys: {
    type: Array,
    default: () => []
  },
  fullMenuList: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['toggle-sub-menu', 'menu-click'])

const route = useRoute()

// 计算是否有可见的子菜单
const hasChildren = computed(() => {
  return props.menu.children?.filter(child => Number(child?.meta?.visible) !== 1).length > 0
})

// 获取可见的子菜单
const visibleChildren = computed(() => {
  return props.menu.children?.filter(child => Number(child?.meta?.visible) !== 1) || []
})

// 检查菜单是否展开
const isOpen = (path) => props.openKeys.includes(path)

/**
 * 函数：isParentOfActivePath
 * 功能：判断当前菜单是否为激活路由的父级（支持非路径前缀的父子关系）。
 * 说明：原逻辑仅依赖路径前缀判断，当父级路径与子级不同（例如"API列表"与其子项不共享路径前缀）时，中间层级不会高亮。
 * 修复：在保持前缀判断的同时，递归检查 menu.children 中是否包含当前激活路由，从而正确高亮中间层级菜单。
 * 参数：parentPath - 父级菜单的路径字符串
 * 返回：Boolean - 是否应当高亮父级菜单
 */
const isParentOfActivePath = (parentPath) => {
  const currentPath = route.path

  // 1) 保留原有的路径前缀判断（适用于真正的嵌套路由场景）
  const isPrefixParent = currentPath.startsWith(parentPath) &&
    (currentPath.length === parentPath.length || currentPath[parentPath.length] === '/')
  if (isPrefixParent) return true

  // 2) 补充：当父级路径与子级不共享前缀时，通过完整的菜单列表递归判断是否为父级
  // 这里需要检查所有子菜单（包括隐藏的菜单项），因为隐藏菜单项仍然可能是当前路由的父级
  const isActiveInDescendants = (node) => {
    if (!node || !Array.isArray(node.children) || node.children.length === 0) return false
    for (const child of node.children) {
      // 检查所有子菜单，包括隐藏的菜单项（不进行visible过滤）
      if (child?.path === currentPath) return true
      if (isActiveInDescendants(child)) return true
    }
    return false
  }

  // 首先检查当前菜单的直接子菜单
  if (isActiveInDescendants(props.menu)) return true

  // 3) 额外检查：如果当前菜单项在完整菜单列表中，检查它是否包含当前激活路由
  // 这可以处理跨层级的情况，比如隐藏菜单项的父级关系
  const findMenuInFullList = (menuList, targetPath) => {
    for (const menu of menuList) {
      if (menu.path === targetPath) return menu
      if (menu.children) {
        const found = findMenuInFullList(menu.children, targetPath)
        if (found) return found
      }
    }
    return null
  }

  const currentMenu = findMenuInFullList(props.fullMenuList, currentPath)
  if (currentMenu) {
    // 检查当前激活菜单的所有父级路径是否包含当前菜单路径
    const checkParentChain = (menu, targetPath) => {
      if (!menu || !menu.meta) return false
      // 如果当前菜单有parentId，在完整菜单列表中查找父级
      if (menu.parentId) {
        const parentMenu = findMenuInFullList(props.fullMenuList, menu.parentId)
        if (parentMenu && parentMenu.path === targetPath) return true
        if (parentMenu) return checkParentChain(parentMenu, targetPath)
      }
      return false
    }
    
    return checkParentChain(currentMenu, parentPath)
  }

  return false
}
</script>

<style scoped>
.menu-item-container {
  width: 100%;
}

.sub-menu-item {
  .sub-menu-title {
    padding: 12px 16px;
    width: 100%;
    min-height: 48px;
    cursor: pointer;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    color: var(--color-black) !important;
    box-sizing: border-box;

    &:hover {
      background: #f5f5f5 !important;
    }

    &.active {
      color: var(--color-primary) !important;
    }

    /* 层级缩进 */
    &.level-1 { padding-left: 24px; }
    &.level-2 { padding-left: 32px; }
    &.level-3 { padding-left: 40px; }
    &.level-4 { padding-left: 48px; }

    .menu-text {
      flex: 1;
      white-space: normal;
      word-break: break-all;
      padding-right: 8px;
    }

    .arrow-icon {
      width: 8px;
      height: 5.333px;
      transition: transform 0.3s ease;
      color: var(--color-black) !important;

      &.active {
        color: var(--color-primary) !important;
      }

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  .sub-menu-children {
    padding-left: 0;
    animation: slideDown 0.3s ease;

    &.closing {
      animation: slideUp 0.3s ease;
    }
  }
}

.menu-item {
  padding: 12px 16px;
  width: 100%;
  min-height: 48px;
  cursor: pointer;
  border-radius: 8px;
  font-size: 16px;
  line-height: 24px;
  color: var(--color-black) !important;
  white-space: normal;
  word-break: break-all;
  box-sizing: border-box;

  &:hover {
    background: #F7F7F7 !important;
  }

  &.active {
    background: rgba(70, 95, 253, 0.1) !important;
    color: var(--color-primary) !important;
  }

  /* 层级缩进 */
  &.level-1 { padding-left: 24px; }
  &.level-2 { padding-left: 32px; }
  &.level-3 { padding-left: 40px; }
  &.level-4 { padding-left: 48px; }
}

@keyframes slideDown {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-10px); }
}
</style>